import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RecordDialog from "@/components/RecordDialog";
import { CLIENT_SECTIONS } from "@/config/clientSections";
import {
  computeReviewStatus, REVIEW_STATUS_STYLE, PRIORITY_STYLE, PROGRESS_STYLE,
} from "@/lib/carePlanConfig";

/**
 * Care Plan person-centred view.
 *
 * Behaviour:
 *  - Fetches all care-plan rows for the client via the standard section CRUD.
 *  - If ANY row carries `is_summary === true`, the landing table shows ONLY
 *    those summary rows (5 for Bayo). Otherwise falls back to the generic
 *    ListView behaviour so every other client is unaffected.
 *  - Clicking a summary row opens a right-hand Sheet with the summary fields
 *    AND the linked legacy subsection cards (rows with matching item.section).
 */
export default function CarePlanView({ clientId, clientName, GenericSectionView }) {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const canManage = user?.role === "admin" || user?.role === "manager";
  const section = CLIENT_SECTIONS["care-planning"];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openSummary, setOpenSummary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/sections/care-planning`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const summaries = useMemo(() => rows.filter((r) => r.is_summary === true), [rows]);
  const legacyBySection = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r?.item?.section;
      if (!r.is_summary && key) map[key] = r;
    });
    return map;
  }, [rows]);

  // If no summaries at all, defer to the generic list behaviour so we don't
  // regress every other client's Care Plan page.
  if (!loading && summaries.length === 0 && GenericSectionView) {
    return <GenericSectionView clientId={clientId} section={section} clientName={clientName} />;
  }

  const submit = async (values) => {
    try {
      if (editing?.id) {
        await api.put(`/clients/${clientId}/sections/care-planning/${editing.id}`, { data: values });
        toast.success("Care plan updated");
      } else {
        await api.post(`/clients/${clientId}/sections/care-planning`, { data: values });
        toast.success("Care plan created");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete summary "${row.title}"?`)) return;
    try {
      await api.delete(`/clients/${clientId}/sections/care-planning/${row.id}`);
      toast.success("Deleted");
      if (openSummary?.id === row.id) setOpenSummary(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-50">
      <div className="h-12 border-b border-slate-200 bg-white px-6 flex items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{clientName || "Service User"}</div>
          <h2 className="font-heading text-base font-semibold" data-testid="section-title">Care Plan</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 text-slate-700" data-testid="section-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-9 bg-sky-700 hover:bg-sky-800" data-testid="section-new">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Record
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="section-loading">Loading…</div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="care-summaries">
            <table className="data-table w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-b border-slate-200">
                  <th className="px-3 py-2 w-8">#</th>
                  <th className="px-3 py-2">Care Plan</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Progress</th>
                  <th className="px-3 py-2">Review Status</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Next Review</th>
                  <th className="px-2 py-2 text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((r, i) => {
                  const status = computeReviewStatus(r.next_review_date);
                  const rs = REVIEW_STATUS_STYLE[status];
                  const prio = PRIORITY_STYLE[r.priority] || "bg-slate-50 text-slate-500 border-slate-200";
                  const prog = PROGRESS_STYLE[r.progress] || "bg-slate-50 text-slate-500 border-slate-200";
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setOpenSummary(r)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`care-row-${r.id}`}
                    >
                      <td className="px-3 py-2 text-slate-400 tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-900" data-testid={`care-title-${r.id}`}>{r.title}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${prio}`}>{r.priority || "—"}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${prog}`}>{r.progress || "—"}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border ${rs.className}`} data-testid={`care-review-${r.id}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${rs.dot}`} /> {rs.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">v{r.version || 1}</td>
                      <td className="px-3 py-2 text-slate-700 tabular-nums">
                        {r.next_review_date
                          ? new Date(r.next_review_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => { setEditing(r); setDialogOpen(true); }} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`edit-${r.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        {canDelete && (
                          <button onClick={() => doDelete(r)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`delete-${r.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecordDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        module={section}
        record={editing}
        onSubmit={submit}
        clientId={clientId}
      />

      <DetailSheet
        row={openSummary}
        legacyBySection={legacyBySection}
        open={!!openSummary}
        onOpenChange={(v) => { if (!v) setOpenSummary(null); }}
        onEdit={(r) => { setEditing(r); setDialogOpen(true); setOpenSummary(null); }}
      />
    </div>
  );
}

/**
 * Defensive scalar renderer.
 * Legacy Bayo rows use nested `item: { section, content, is_demo, ... }`.
 * The UI must NEVER render such an object as a React child. This helper
 * coerces any value into a safe string (or React fallback).
 */
export function safeText(value) {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((v) => safeText(v)).join(", ");
  }
  if (typeof value === "object") {
    // Prefer intended nested fields when present.
    if (typeof value.content === "string") return value.content;
    if (typeof value.text === "string") return value.text;
    if (typeof value.name === "string") return value.name;
    if (typeof value.title === "string") return value.title;
    if (typeof value.label === "string") return value.label;
    return "—";
  }
  return String(value);
}

const PRETTY_SECTION = {
  communication: "Communication",
  personal_care: "Personal Care",
  medication: "Medication",
  mobility: "Mobility",
  nutrition: "Nutrition",
  emotional_wellbeing: "Emotional Wellbeing",
  community: "Community",
  risks: "Risks",
  outcomes: "Outcomes",
};

function DetailSheet({ row, legacyBySection, open, onOpenChange, onEdit }) {
  if (!row) return null;
  const status = computeReviewStatus(row.next_review_date);
  const rs = REVIEW_STATUS_STYLE[status];
  const prio = PRIORITY_STYLE[row.priority] || "bg-slate-50 text-slate-500 border-slate-200";
  const prog = PROGRESS_STYLE[row.progress] || "bg-slate-50 text-slate-500 border-slate-200";
  const linked = Array.isArray(row.linked_sections) ? row.linked_sections : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-xl overflow-y-auto" data-testid="care-detail">
        <SheetHeader>
          <SheetTitle className="font-heading text-lg" data-testid="care-detail-title">{row.title}</SheetTitle>
        </SheetHeader>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${prio}`}>Priority: {row.priority || "—"}</span>
          <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${prog}`}>Progress: {row.progress || "—"}</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border ${rs.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${rs.dot}`} /> {rs.label}
          </span>
          {row.next_review_date && (
            <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border bg-slate-50 text-slate-700 border-slate-200 tabular-nums">
              Next review: {new Date(row.next_review_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </span>
          )}
        </div>

        <div className="mt-6 space-y-5 text-sm">
          <Block label="Person-centred summary" value={safeText(row.summary)} testid="care-detail-summary" />
          <Block label="What matters to me (person voice)" value={safeText(row.person_voice)} />
          <Block label="Desired outcomes" value={safeText(row.desired_outcomes)} />
          <Block label="Interventions / support required" value={safeText(row.interventions)} />
          {row.reviewed_by && (
            <div className="text-[11px] text-slate-500">
              Last reviewed by <span className="font-medium text-slate-700">{row.reviewed_by}</span>
              {row.reviewed_date && <> on <span className="tabular-nums">{new Date(row.reviewed_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span></>}
            </div>
          )}
        </div>

        {linked.length > 0 && (
          <div className="mt-8">
            <h3 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2" data-testid="care-linked-header">
              Detailed subsections
            </h3>
            <div className="space-y-2">
              {linked.map((key) => {
                const legacy = legacyBySection[key];
                return (
                  <article key={key} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`care-linked-${key}`}>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      {PRETTY_SECTION[key] || key}
                    </div>
                    <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">
                      {legacy?.item?.content
                        ? safeText(legacy.item.content)
                        : <span className="text-slate-400">No content recorded yet.</span>}
                    </div>
                    {legacy && (
                      <div className="mt-1.5 text-[10px] text-slate-400 tabular-nums">
                        v{legacy.version || 1} · last updated {legacy.updated_at?.slice(0, 10)}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-2">
          <Button className="flex-1 bg-sky-700 hover:bg-sky-800" onClick={() => onEdit(row)} data-testid="care-detail-edit">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit summary
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="care-detail-close">Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Block({ label, value, testid }) {
  // `value` MUST already be a scalar string via safeText() at the call site.
  // The extra typeof guard defends against future callers that forget to wrap.
  const rendered = (typeof value === "string" || typeof value === "number")
    ? String(value)
    : safeText(value);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-slate-800 whitespace-pre-wrap" data-testid={testid}>
        {rendered && rendered !== "—" ? rendered : <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}
